"use client";

/**
 * AdminPagination — 표 페이지네이션 (우선순위 7)
 *
 * 완전 제어(controlled) 컴포넌트라 서버/클라이언트 페이징 모두에 쓸 수 있다.
 * - 왼쪽: "몇 개씩 보기" page size 선택
 * - 오른쪽: 이전/다음 + 페이지 번호 + "바로 페이지 이동" 입력
 *
 * @example
 * <AdminPagination
 *   page={page} pageSize={pageSize} total={total}
 *   onPageChange={setPage}
 *   onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
 * />
 */
import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export interface AdminPaginationProps {
  /** 현재 페이지 (1부터 시작) */
  page: number;
  /** 페이지당 항목 수 */
  pageSize: number;
  /** 전체 항목 수 */
  total: number;
  /** 페이지 변경 콜백 */
  onPageChange: (page: number) => void;
  /** page size 변경 콜백. 주어지면 "몇 개씩 보기" 선택이 표시된다 */
  onPageSizeChange?: (size: number) => void;
  /** page size 후보 (기본 [10, 20, 50, 100]) */
  pageSizeOptions?: number[];
  /** "바로 이동" 입력 표시 (기본 true) */
  showJump?: boolean;
  className?: string;
}

/** 페이지 번호 목록을 생성한다. (양끝 + 현재 주변, 나머지는 생략표시) */
function buildPages(
  current: number,
  totalPages: number
): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = [];
  const window = 1;
  for (let p = 1; p <= totalPages; p++) {
    if (
      p === 1 ||
      p === totalPages ||
      (p >= current - window && p <= current + window)
    ) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis");
    }
  }
  return pages;
}

const selectClass =
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

export function AdminPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showJump = true,
  className,
}: AdminPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const [jump, setJump] = useState("");

  const goto = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== current) onPageChange(next);
  };

  const submitJump = () => {
    const n = Number(jump);
    if (Number.isFinite(n) && n >= 1) {
      goto(Math.floor(n));
      setJump("");
    }
  };

  const pages = buildPages(current, totalPages);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-x-4 gap-y-3",
        className
      )}
    >
      {/* 페이지당 몇 개 보기 (오른쪽 정렬) */}
      {onPageSizeChange && (
        <div className="flex items-center gap-2 bg-white text-sm text-muted-foreground">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={selectClass}
            aria-label="페이지당 항목 수"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}개씩 보기
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 페이지 네비게이션 */}
      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          onClick={() => goto(1)}
          disabled={current <= 1}
          aria-label="첫 페이지"
        >
          <ChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => goto(current - 1)}
          disabled={current <= 1}
          aria-label="이전 페이지"
        >
          <ChevronLeft className="size-4" />
        </Button>

        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span
              key={`e-${i}`}
              className="px-1.5 text-sm text-muted-foreground"
            >
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === current ? "default" : "outline"}
              size="icon"
              onClick={() => goto(p)}
              aria-current={p === current ? "page" : undefined}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="icon"
          onClick={() => goto(current + 1)}
          disabled={current >= totalPages}
          aria-label="다음 페이지"
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => goto(totalPages)}
          disabled={current >= totalPages}
          aria-label="마지막 페이지"
        >
          <ChevronsRight className="size-4" />
        </Button>

        {showJump && (
          <div className="ml-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Input
              type="number"
              min={1}
              max={totalPages}
              value={jump}
              onChange={(e) => setJump(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitJump();
                }
              }}
              placeholder={String(current)}
              className="h-9 w-16 text-center"
              aria-label="이동할 페이지 번호"
            />
            <Button type="button" variant="outline" onClick={submitJump}>
              이동
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
